import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'antd';
import { TaskInventory as TaskInventoryType, BaseTaskInventory } from '@bringg/types';
import { Translations as TaskInventoryTranslations } from '../task-inventory/task-inventory';
import { debounce as _debounce, cloneDeep as _cloneDeep } from 'lodash';
const { Search } = Input;

export interface Translations extends TaskInventoryTranslations {}

const DEBOUNCE_WAIT: number = 300;
interface allTaskInventoriesInWayPoint {
	[key: number]: TaskInventoryType;
}
interface TaskInventoriesSearchProps {
	taskInventories?: TaskInventoryType[];
	allTaskInventoriesInWayPoint?: allTaskInventoriesInWayPoint;
	getAllTaskInventoryChildren?: (inventory: TaskInventoryType) => TaskInventoryType[];
	setFilteredInventories?: (taskInventories: TaskInventoryType[], shouldExpand: boolean) => void;
	changeActiveKey?: (key: string[]) => void;
	translations: Translations;
}

const TaskInventoriesSearch: React.FC<TaskInventoriesSearchProps> = ({
	taskInventories,
	allTaskInventoriesInWayPoint,
	getAllTaskInventoryChildren,
	setFilteredInventories,
	changeActiveKey,
	translations
}) => {
	const searchElement = useRef(null);
	const [shouldUpdateFiltered, setShouldUpdateFiltered] = useState(false);

	useEffect(() => {
		if (searchElement?.current.input.value) {
			setShouldUpdateFiltered(true);
		}
	}, [taskInventories]);

	useEffect(() => {
		if (shouldUpdateFiltered) {
			handleSearch(searchElement?.current.input.value);
			setShouldUpdateFiltered(false);
		}
	}, [shouldUpdateFiltered]);

	const getAllParentsIds = (child: BaseTaskInventory, parents: string[]) => {
		if (!child.parent_task_inventory_id) return;
		parents.push(child.parent_task_inventory_id.toString());
		const parent = allTaskInventoriesInWayPoint[child.parent_task_inventory_id];
		getAllParentsIds(parent, parents);
	};

	const doesItemIncludeSearchValue = (item: TaskInventoryType, searchVal: string): boolean => {
		searchVal = searchVal.toLocaleLowerCase();
		return (
			item?.external_id?.toLocaleLowerCase().includes(searchVal) ||
			item?.scan_string?.toLocaleLowerCase().includes(searchVal) ||
			item?.name?.toLocaleLowerCase().includes(searchVal)
		);
	};

	const handleSearch = (searchValue: string) => {
		let shouldExpand: boolean = false;
		if (!searchValue) {
			setFilteredInventories(taskInventories, shouldExpand);
			changeActiveKey([]);
			return;
		}

		const searchResults: TaskInventoryType[] = [];
		const rootParentsSearchResults: TaskInventoryType[] = taskInventories.filter(item => {
			const itemChildren = getAllTaskInventoryChildren(item);
			const filteredChildren = itemChildren.filter(child => {
				return doesItemIncludeSearchValue(child, searchValue) && searchResults.push(child);
			});

			return doesItemIncludeSearchValue(item, searchValue) || filteredChildren.length;
		});

		const allParentsIdsToExpand: string[] = [];

		searchResults.forEach(taskInventory => {
			getAllParentsIds(taskInventory, allParentsIdsToExpand);
		});
		shouldExpand = !!allParentsIdsToExpand.length;
		setFilteredInventories(rootParentsSearchResults, shouldExpand);
		changeActiveKey(allParentsIdsToExpand);
	};

	const debounceHandleSearch = useMemo(() => {
		return _debounce(handleSearch, DEBOUNCE_WAIT);
	}, [taskInventories]);

	return (
		<div className="search-container">
			<Search
				ref={searchElement}
				className="search-component"
				onChange={e => debounceHandleSearch(e.target.value)}
				placeholder={translations.searchPlaceholder}
			/>
		</div>
	);
};

export default TaskInventoriesSearch;
